import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, User as FirebaseUser, getRedirectResult } from 'firebase/auth';
import { auth } from '../services/firebase';
import { User } from '../types';

interface UseAuthStateReturn {
  firebaseUser: FirebaseUser | null;
  currentUser: User | null;
  loading: boolean;
  setCurrentUser: React.Dispatch<React.SetStateAction<User | null>>;
}

export const useAuthState = (users: User[], usersLoaded: boolean): UseAuthStateReturn => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Auth Listener
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user);
      if (!user) {
        setCurrentUser(null);
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  // Handle redirect result (for failed popups that fell back, or direct redirect usage)
  useEffect(() => {
    getRedirectResult(auth)
      .then((result) => {
        if (result) {
          console.log("Redirect auth result:", result.user.uid);
          // Set user immediately if not already picked up by onAuthStateChanged
          setFirebaseUser(result.user);
        }
      })
      .catch((error) => {
        // This typically catches the "Pending promise was never set" if it happens during redirect resolution
        // or other auth errors.
        console.warn("Auth redirect error encountered (handled):", error);
        if (error.code === 'auth/internal-error' && error.message.includes('Pending promise')) {
          // Suppress this specific internal assertion error as it's often a race condition
          // that resolves itself on retry or next action.
          console.log("Suppressed internal auth assertion error.");
        }
      });
  }, []);

  // Sync currentUser when users or firebaseUser changes
  useEffect(() => {
    if (firebaseUser) {
      if (usersLoaded) {
        const foundMe = users.find(u => u.id === firebaseUser.uid);
        if (foundMe) {
          queueMicrotask(() => {
            setCurrentUser(prev => {
              if (!prev) return foundMe;
              return { ...foundMe, activities: prev.activities || [] };
            });
            setLoading(false);
          });
        } else {
          console.log("User authenticated but profile not found yet.");
          queueMicrotask(() => setLoading(false));
        }
      }
    }
  }, [firebaseUser, users, usersLoaded]);

  return { firebaseUser, currentUser, loading, setCurrentUser };
};
